/**
 * Подсказка по опечатке в адресе почты.
 *
 * Зачем. Регистрация идёт по одной почте, без пароля и без подтверждения
 * адреса: вход потом — по ссылке из письма. Значит опечатка в домене
 * означает аккаунт, в который человек больше никогда не войдёт — письма
 * уходят в никуда, а сменить адрес ни в кабинете, ни в админке нельзя.
 *
 * Почему не только словарь. Первая версия сверялась со списком заранее
 * перечисленных опечаток, и живая регистрация владельца прошла с адресом
 * на «main.ru» — такой опечатки в списке не было, подсказка молчала.
 * Перечислить все опечатки нельзя, поэтому домен сравнивается с белым
 * списком популярных почт по расстоянию Дамерау (замена, вставка, пропуск
 * и перестановка двух соседних букв — ровно то, что делают пальцы).
 *
 * Подсказка НЕ блокирует отправку: она может ошибиться, а живой адрес на
 * редком домене должен пройти. Человек либо принимает подсказку одним
 * нажатием, либо отправляет как есть.
 */

// Популярные почты, с которыми сверяемся. Точное совпадение — подсказки
// нет. mail.com здесь не ради популярности: без него живой mail.com
// «исправлялся» бы в gmail.com (расстояние 1).
const KNOWN_DOMAINS = [
  'mail.ru', 'bk.ru', 'inbox.ru', 'list.ru', 'internet.ru',
  'yandex.ru', 'ya.ru', 'rambler.ru',
  'gmail.com', 'icloud.com', 'me.com', 'outlook.com', 'hotmail.com',
  'yahoo.com', 'mail.com',
];

// Ошибочно набранные зоны → верная. Только заведомо НЕсуществующие
// написания: живые ccTLD (ua, by, kz, de) сюда попадать не должны, иначе
// настоящий mail.ua «исправился» бы в mail.ru.
const TYPO_ZONES = {
  ru: ['ri', 'ry', 'rru', 'tu', 'u', 'r', 'rur', 'ruu'],
  com: ['con', 'cm', 'co', 'comm', 'xom', 'cpm', 'om', 'coom'],
};

// Формат адреса. Нарочно простой: одна собака, точка в домене, без
// пробелов. Задача проверки — отсечь явно негодное, а не пересказывать
// RFC: строгая регулярка отвергает живые редкие адреса, и это хуже.
const EMAIL_RE = /^[^\s@]+@[^\s@.]+\.[^\s@]{2,}$/;

export function isEmailShaped(value) {
  return EMAIL_RE.test(String(value || '').trim());
}

/** Расстояние Дамерау — Левенштейна между двумя строками. */
function damerau(a, b) {
  const m = a.length;
  const n = b.length;
  const d = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) d[i][0] = i;
  for (let j = 0; j <= n; j++) d[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      // Перестановка двух соседних букв — одна ошибка, а не две: «mali» от
      // «mail» отличается ровно этим, и по Левенштейну это стоило бы 2.
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[m][n];
}

/** Домен → [имя, зона]. Зона — то, что после последней точки. */
function splitDomain(domain) {
  const i = domain.lastIndexOf('.');
  return i < 0 ? [domain, ''] : [domain.slice(0, i), domain.slice(i + 1)];
}

/**
 * Верный домен вместо похожего на него — или пустая строка.
 *
 * Две отдельные проверки вместо одной «≤2 по всему домену»: целиком по
 * домену порог 2 «исправлял» бы живые адреса вроде mail.ua в mail.ru.
 *   1) зона набрана заведомо неверно, имя совпадает: gmail.con → gmail.com;
 *   2) зона верная, ошибка в имени: main.ru → mail.ru. Порог зависит от
 *      длины имени: короткие (ya, bk, me) правим только на расстоянии 1,
 *      иначе в них «исправлялось» бы что угодно.
 * Ничья двух кандидатов на одном расстоянии — подсказки нет: угадывать
 * наугад хуже, чем молчать.
 */
export function suggestDomainFix(rawDomain) {
  const domain = String(rawDomain || '').trim().toLowerCase();
  if (!domain || KNOWN_DOMAINS.includes(domain)) return '';
  const [sld, tld] = splitDomain(domain);
  if (!sld || !tld) return '';

  for (const [goodZone, badZones] of Object.entries(TYPO_ZONES)) {
    if (badZones.includes(tld) && KNOWN_DOMAINS.includes(`${sld}.${goodZone}`)) {
      return `${sld}.${goodZone}`;
    }
  }

  const candidates = [];
  for (const known of KNOWN_DOMAINS) {
    const [knownSld, knownTld] = splitDomain(known);
    if (knownTld !== tld) continue;
    const dist = damerau(sld, knownSld);
    const maxDist = Math.max(knownSld.length, sld.length) <= 5 ? 1 : 2;
    if (dist > 0 && dist <= maxDist) candidates.push([dist, known]);
  }
  if (!candidates.length) return '';
  candidates.sort((a, b) => a[0] - b[0]);
  if (candidates.length > 1 && candidates[0][0] === candidates[1][0]) return '';
  return candidates[0][1];
}

/**
 * Исправленный адрес — или пустая строка, если исправлять нечего.
 * Часть до собаки остаётся как есть: в ней опечатку не угадать.
 */
export function suggestEmailFix(value) {
  const raw = String(value || '').trim();
  const at = raw.lastIndexOf('@');
  if (at <= 0) return '';
  const local = raw.slice(0, at);
  const fixed = suggestDomainFix(raw.slice(at + 1));
  return fixed ? `${local}@${fixed}` : '';
}
